import { useEffect, useState } from 'react';

import { CircleIcon, Delete02Icon, PencilEdit01Icon, ZapIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { getCheckTypeIcon } from 'helpers/iconsHelper';
import { Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { RenewalConfigModal } from './components/RenewalConfigModal/RenewalConfigModal';
import Alert from 'components/Alert/Alert';
import { Button, SearchInput } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';

import {
  useCreateRenewalConfigMutation,
  useDeleteRenewalConfigMutation,
  useUpdateRenewalConfigMutation,
} from 'api/renewals-config-api';

export const RenewalsConfigurationTab = ({
  companyChecks = [],
  renewalConfigs = [],
  companyUsers = [],
  brands = [],
  setLoading = () => {},
  updateLocalRenewalConfigs = () => {},
}) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [createRenewalConfig] = useCreateRenewalConfigMutation();
  const [updateRenewalConfig] = useUpdateRenewalConfigMutation();
  const [deleteRenewalConfig] = useDeleteRenewalConfigMutation();

  const [query, setQuery] = useState('');
  const [selectedRenewalConfig, setSelectedRenewalConfig] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [filteredRenewalConfigs, setFilteredRenewalConfigs] = useState(renewalConfigs);

  useEffect(() => {
    setFilteredRenewalConfigs(
      renewalConfigs.filter(rc => rc.name.toLowerCase().includes(query.toLowerCase())),
    );
  }, [query, renewalConfigs]);

  const onEdit = renewalConfig => {
    setSelectedRenewalConfig(renewalConfig);
    setShowEditModal(true);
  };

  const saveRenewalConfig = renewalConfigData => {
    setLoading(true);
    setShowEditModal(false);
    if (renewalConfigData.id) {
      updateRenewalConfig({ id: renewalConfigData.id, data: renewalConfigData });
      updateLocalRenewalConfigs(prev =>
        prev.map(rc => (rc.id === renewalConfigData.id ? renewalConfigData : rc)),
      );
    } else {
      createRenewalConfig(renewalConfigData);
      updateLocalRenewalConfigs(prev => [...prev, renewalConfigData]);
    }
    setQuery('');
    setLoading(false);
  };

  const handleDeleteRenewalConfig = async renewalConfigId => {
    setLoading(true);
    try {
      await deleteRenewalConfig(renewalConfigId).unwrap();
      updateLocalRenewalConfigs(prev => prev.filter(rc => rc.id !== renewalConfigId));
      setQuery('');
    } catch (error) {
      console.error('Failed to delete renewal config:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="u-padding--large">
      <RenewalConfigModal
        companyUsers={companyUsers}
        brands={brands}
        renewalConfig={selectedRenewalConfig}
        companyChecks={companyChecks}
        visible={showEditModal}
        onSubmit={saveRenewalConfig}
        onClose={() => {
          setShowEditModal(false);
          setSelectedRenewalConfig(false);
        }}
      />
      <div
        className={cn(
          'card',
          'card-with-border',
          'u-padding',
          'd-flex',
          'justify-content-between',
        )}>
        <SearchInput placeholder="Search..." className="u-width-25" onChange={setQuery} />
        <div className="d-flex">
          <Button
            icon="plus"
            onClick={() => {
              setSelectedRenewalConfig(null);
              setShowEditModal(true);
            }}>
            {t('renewals.tabs.configuration.create_renewal_type')}
          </Button>
          <Button
            icon="file"
            type="secondary"
            onClick={() => navigate('/bulk_import/renewals')}
            className="u-margin-left--small">
            {t('renewals.tabs.configuration.import_renewals')}
          </Button>
        </div>
      </div>
      <div className="u-margin-top--large">
        {filteredRenewalConfigs.map(renewalConfig => {
          const companyCheck = companyChecks.find(cc => cc.type === renewalConfig.type);
          return (
            <RenewalConfigRow
              key={renewalConfig.id}
              companyCheck={companyCheck}
              renewalConfig={renewalConfig}
              onEdit={onEdit}
              onDelete={handleDeleteRenewalConfig}
            />
          );
        })}
      </div>
    </div>
  );
};

export const RenewalConfigRow = ({
  companyCheck,
  renewalConfig,
  onEdit = () => {},
  onDelete = () => {},
}) => {
  const { t } = useTranslation();
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);

  const handleDelete = () => {
    setShowDeleteAlert(false);
    onDelete(renewalConfig.id);
  };

  return (
    <>
      <Alert
        show={showDeleteAlert}
        title={`Are you sure you want to delete "${renewalConfig.name}"?`}
        message="This action cannot be undone. All configurations associated with this renewal type will be permanently removed."
        ok="Delete"
        type="error"
        onOk={handleDelete}
        icon={Delete02Icon}
        cancel="Cancel"
        onCancel={() => setShowDeleteAlert(false)}
      />
      <div className={cn('card', 'card-with-border', 'u-padding', 'u-margin-bottom')}>
        <div className={cn('d-flex', 'justify-content-between', 'align-items-center')}>
          <div className={cn('d-flex', 'align-items-center')}>
            <IconComponent icon={getCheckTypeIcon(renewalConfig.type)} className="u-margin-right" />
            <div>
              <h1 className="title-4">{renewalConfig.name}</h1>
              <p className={cn('t-small', 'u-margin-0')}>{companyCheck?.check_type?.title}</p>
            </div>
          </div>
          <div className={cn('d-flex', 'align-items-center')}>
            <Badge className="u-status-primary u-capitalize u-font-size-small">
              {renewalConfig.renewal_mode === 'conditional'
                ? 'Conditional'
                : renewalConfig.renewal_mode === 'use_expiration'
                  ? 'Expiration date'
                  : 'Fixed Cadence'}
            </Badge>
            {renewalConfig.auto_renew ? (
              <Badge
                className={cn(
                  'u-status-completed',
                  'u-capitalize',
                  'u-font-size-small',
                  'u-margin-left',
                  'd-inline-flex',
                  'align-items-center',
                )}>
                <HugeiconsIcon icon={ZapIcon} size={16} className="u-margin-right--small" />
                {t('renewals.auto_renew')}
              </Badge>
            ) : (
              <Badge
                className={cn(
                  'u-status-canceled',
                  'u-capitalize',
                  'u-font-size-small',
                  'u-margin-left',
                  'd-inline-flex',
                  'align-items-center',
                )}>
                <HugeiconsIcon icon={CircleIcon} size={16} className="u-margin-right--small" />
                {t('renewals.manual_renew')}
              </Badge>
            )}
            <Button
              className="u-margin-left"
              type="secondary"
              small
              onClick={() => onEdit(renewalConfig)}>
              <HugeiconsIcon icon={PencilEdit01Icon} size={16} className="u-margin-right--small" />
              {t('common.edit')}
            </Button>
            <Button
              className="u-margin-left"
              type="delete"
              small
              onClick={() => setShowDeleteAlert(true)}>
              <HugeiconsIcon icon={Delete02Icon} size={16} className="u-margin-right--small" />
              {t('common.delete')}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
